import { AppMenuItem, MenuModel } from "../types/layout";

export function isRouteMatch(pathname: string, to?: string): boolean {
    if (!to) {
        return false;
    }

    if (pathname === to) {
        return true;
    }

    if (to === "/") {
        return false;
    }

    return pathname.startsWith(`${to}/`);
}

export function getMenuChildren(item?: AppMenuItem | MenuModel): AppMenuItem[] {
    return (item?.items ?? []).filter((child) => Boolean(child?.label)) as AppMenuItem[];
}

export function itemHasDropdown(item?: AppMenuItem | MenuModel): boolean {
    return getMenuChildren(item).length > 0;
}

export function findActiveMenuKey(
    items: MenuModel[] | AppMenuItem[],
    pathname: string,
    parentKey = ""
): string | null {
    for (let index = 0; index < items.length; index += 1) {
        const item = items[index] as AppMenuItem;

        if (!item?.label) {
            continue;
        }

        const key = parentKey ? `${parentKey}-${index}` : String(index);

        if (item.to && isRouteMatch(pathname, item.to)) {
            return key;
        }

        const children = getMenuChildren(item);
        if (children.length) {
            const childKey = findActiveMenuKey(children, pathname, key);
            if (childKey) {
                return childKey;
            }
        }
    }

    return null;
}

export function findActiveMenuKeyInSections(
    sections: MenuModel[],
    pathname: string
): string | null {
    for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex += 1) {
        const sectionItems = getMenuChildren(sections[sectionIndex]);
        const key = findActiveMenuKey(sectionItems, pathname, String(sectionIndex));

        if (key) {
            return key;
        }
    }

    return null;
}

export function findActiveMenuTrail(
    sections: MenuModel[],
    pathname: string
): { section?: string; label?: string; icon?: string } | null {
    const walkItems = (
        items: AppMenuItem[],
        sectionLabel?: string
    ): { section?: string; label?: string; icon?: string } | null => {
        for (const item of items) {
            const children = getMenuChildren(item);

            if (children.length) {
                const nestedMatch = walkItems(children, sectionLabel);
                if (nestedMatch) {
                    return nestedMatch;
                }
            }

            if (item.to && isRouteMatch(pathname, item.to)) {
                return {
                    section: sectionLabel,
                    label: item.label,
                    icon: item.icon,
                };
            }
        }

        return null;
    };

    for (const section of sections) {
        const sectionItems = getMenuChildren(section);
        const match = walkItems(sectionItems, section.label);

        if (match) {
            return match;
        }
    }

    return null;
}

export function sanitizeMenuItems(items: MenuModel[] = []): MenuModel[] {
    return items
        .filter((item) => Boolean(item?.label))
        .map((item) => {
            const children = sanitizeMenuItems(getMenuChildren(item));

            return {
                ...item,
                items: children.length ? children : undefined,
            };
        });
}

export function sanitizeMenuSections(sections: MenuModel[] = []): MenuModel[] {
    return sections
        .map((section) => ({
            ...section,
            items: sanitizeMenuItems(getMenuChildren(section)),
        }))
        .filter((section) => getMenuChildren(section).length > 0);
}
